import React, { useEffect, useState } from 'react';
import { XIcon, CheckIcon, AlertTriangleIcon } from 'lucide-react';
interface StatusChangeModalProps {
  settlement: {
    id: string;
    status: string;
    statusHistory?: any[];
  };
  onClose: () => void;
  onStatusChange: (newStatus: string, reason: string) => void;
}
export const StatusChangeModal: React.FC<StatusChangeModalProps> = ({
  settlement,
  onClose,
  onStatusChange
}) => {
  const [newStatus, setNewStatus] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [changeSuccess, setChangeSuccess] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [validationError, setValidationError] = useState('');
  // Available status transitions based on current status
  const availableStatusOptions = (() => {
    switch (settlement.status) {
      case 'failed':
        return [{
          value: 'processing',
          label: 'Retry Processing'
        }, {
          value: 'cancelled',
          label: 'Cancel'
        }];
      case 'processing':
        return [{
          value: 'completed',
          label: 'Mark as Completed'
        }, {
          value: 'failed',
          label: 'Mark as Failed'
        }];
      case 'pending':
        return [{
          value: 'processing',
          label: 'Start Processing'
        }, {
          value: 'cancelled',
          label: 'Cancel'
        }];
      case 'completed':
        return [];
      // No status changes allowed
      default:
        return [{
          value: 'pending',
          label: 'Set as Pending'
        }, {
          value: 'processing',
          label: 'Start Processing'
        }, {
          value: 'completed',
          label: 'Mark as Completed'
        }, {
          value: 'failed',
          label: 'Mark as Failed'
        }, {
          value: 'cancelled',
          label: 'Cancel'
        }];
    }
  })();
  // Set initial status
  useEffect(() => {
    if (availableStatusOptions.length > 0) {
      setNewStatus(availableStatusOptions[0].value);
    }
  }, []);
  // Check if reason is required
  const isReasonRequired = ['failed', 'cancelled'].includes(newStatus);
  const handleStatusChange = () => {
    // Validation
    if (isReasonRequired && !reason.trim()) {
      setValidationError('Please provide a reason for this status change');
      return;
    }
    if (!confirmed) {
      setValidationError('Please confirm this status change');
      return;
    }
    setValidationError('');
    // For irreversible changes, show confirmation dialog
    if (['completed', 'cancelled'].includes(newStatus) && !showConfirmation) {
      setShowConfirmation(true);
      return;
    }
    setIsSubmitting(true);
    // Simulate status change
    setTimeout(() => {
      setIsSubmitting(false);
      setChangeSuccess(true);
      // Close after showing success message
      setTimeout(() => {
        onStatusChange(newStatus, reason);
      }, 1500);
    }, 1500);
  };
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'processing':
        return 'Processing';
      case 'pending':
        return 'Pending';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Handle click outside to close
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isSubmitting && !changeSuccess) {
      onClose();
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={handleBackdropClick} data-id="element-741">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-742">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-743">
          <h2 className="text-xl font-semibold text-gray-800" data-id="element-744">
            Change Settlement Status
          </h2>
          {!isSubmitting && !changeSuccess && <button onClick={onClose} className="text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-745">
              <XIcon className="h-5 w-5" data-id="element-746" />
            </button>}
        </div>
        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-8rem)]" data-id="element-747">
          {changeSuccess ? <div className="flex flex-col items-center justify-center py-8" data-id="element-748">
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-749">
                <CheckIcon className="h-6 w-6 text-green-600" data-id="element-750" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-751">
                Status updated successfully
              </h3>
              <p className="text-gray-500 text-center" data-id="element-752">
                Settlement {settlement.id} has been updated to{' '}
                <span className={`font-medium ${getStatusBadgeClass(newStatus).replace('bg-', 'text-').replace('-100', '-600')}`} data-id="element-753">
                  {getStatusLabel(newStatus)}
                </span>
              </p>
            </div> : showConfirmation ? <div className="py-4" data-id="element-754">
              <div className="flex items-center justify-center mb-4" data-id="element-755">
                <div className="h-12 w-12 bg-yellow-100 rounded-full flex items-center justify-center" data-id="element-756">
                  <AlertTriangleIcon className="h-6 w-6 text-yellow-600" data-id="element-757" />
                </div>
              </div>
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2" data-id="element-758">
                Are you sure?
              </h3>
              <p className="text-gray-500 text-center mb-4" data-id="element-759">
                Changing the status to{' '}
                <span className="font-medium" data-id="element-760">{getStatusLabel(newStatus)}</span>{' '}
                is irreversible. Are you sure you want to continue?
              </p>
              <div className="flex justify-center space-x-3" data-id="element-761">
                <button onClick={() => setShowConfirmation(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-762">
                  Cancel
                </button>
                <button onClick={handleStatusChange} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors" data-id="element-763">
                  Yes, Change Status
                </button>
              </div>
            </div> : <div className="space-y-4" data-id="element-764">
              <div data-id="element-765">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-766">
                  Current Status
                </label>
                <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(settlement.status)}`} data-id="element-767">
                  {getStatusLabel(settlement.status)}
                </div>
              </div>
              {settlement.status === 'completed' ? <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4" data-id="element-768">
                  <p className="text-sm text-yellow-700" data-id="element-769">
                    Completed settlements cannot be changed to a different
                    status.
                  </p>
                </div> : <>
                  <div data-id="element-770">
                    <label htmlFor="newStatus" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-771">
                      New Status
                    </label>
                    <select id="newStatus" value={newStatus} onChange={e => setNewStatus(e.target.value)} className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" data-id="element-772">
                      {availableStatusOptions.map(option => <option key={option.value} value={option.value} data-id="element-773">
                          {option.label}
                        </option>)}
                    </select>
                  </div>
                  {isReasonRequired && <div data-id="element-774">
                      <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-775">
                        Reason for Change{' '}
                        <span className="text-red-500" data-id="element-776">*</span>
                      </label>
                      <textarea id="reason" value={reason} onChange={e => setReason(e.target.value)} rows={3} className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder={`Please provide a reason for ${newStatus === 'failed' ? 'failure' : 'cancellation'}`} data-id="element-777" />
                    </div>}
                  <div data-id="element-778">
                    <label className="flex items-center" data-id="element-779">
                      <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-780" />
                      <span className="ml-2 text-sm text-gray-700" data-id="element-781">
                        I confirm this status change
                      </span>
                    </label>
                  </div>
                  {validationError && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md" data-id="element-782">
                      {validationError}
                    </div>}
                </>}
            </div>}
        </div>
        {/* Footer */}
        {!changeSuccess && !showConfirmation && <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3" data-id="element-783">
            <button onClick={onClose} disabled={isSubmitting} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-784">
              Cancel
            </button>
            {settlement.status !== 'completed' && <button onClick={handleStatusChange} disabled={isSubmitting} className={`px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white ${isSubmitting ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} transition-colors flex items-center`} data-id="element-785">
                {isSubmitting ? <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" data-id="element-786"></div>
                    Updating...
                  </> : 'Change Status'}
              </button>}
          </div>}
      </div>
    </div>;
};